
import { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { authRateLimiter } from '@/utils/rateLimiting';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: any | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Session timeout management
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
  
  // Auto logout function
  const autoLogout = useCallback(async () => {
    console.log('Auto logout triggered due to inactivity');
    await signOut();
  }, []);
  
  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (user) {
      timeoutRef.current = setTimeout(autoLogout, INACTIVITY_TIMEOUT);
    }
  }, [user, autoLogout, INACTIVITY_TIMEOUT]);
  
  // Track user activity
  useEffect(() => {
    if (!user) return;
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };
    
    // Add event listeners for user activity
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    // Start the timer initially
    resetInactivityTimer();
    
    // Cleanup on unmount or when user changes
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, resetInactivityTimer]);
  
  // Handle page visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, clear the timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      } else {
        // Page is visible again, restart the timer
        if (user) {
          resetInactivityTimer();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resetInactivityTimer]);
  
  // Handle beforeunload to sign out when browser/tab closes
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (user) {
        // Use navigator.sendBeacon for more reliable logout on page unload
        supabase.auth.signOut();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user]);

  useEffect(() => {
    // Set up auth state listener with non-async callback
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Handle profile fetching in a separate function to avoid async callback issues
          fetchUserProfile(session.user);
        } else {
          // No session/user - clear profile and set loading to false
          setProfile(null);
          setLoading(false);
        }
      }
    );

    // Get initial session
    const initializeSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Initial session fetch error:', error);
          setLoading(false);
          return;
        }
        
        // If no session, set loading to false immediately
        if (!session) {
          setLoading(false);
        }
        // If there is a session, the onAuthStateChange will handle it
      } catch (error) {
        console.error('Session initialization error:', error);
        setLoading(false);
      }
    };

    initializeSession();

    // CRITICAL CLEANUP: Unsubscribe when component unmounts
    return () => {
      subscription.unsubscribe();
    };
  }, []); // Empty dependency array to prevent re-initialization

  // Separate function to handle profile fetching
  const fetchUserProfile = async (user: User) => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Profile fetch error:', error);
        setProfile({ 
          id: user.id, 
          email: user.email,
          role: 'viewer'
        });
      } else if (profileData) {
        setProfile(profileData);
      } else {
        // User exists but no profile record - create minimal profile with viewer role
        console.warn('User authenticated but no profile found, creating minimal profile');
        setProfile({ 
          id: user.id, 
          email: user.email,
          role: 'viewer'
        });
      }
    } catch (error) {
      console.error('Profile fetch failed:', error);
      setProfile({ 
        id: user.id, 
        email: user.email,
        role: 'viewer'
      });
    } finally {
      // Always set loading to false after profile processing for authenticated users
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const identifier = email.toLowerCase();
    
    // Check rate limiting
    if (authRateLimiter.isBlocked(identifier)) {
      const remainingTime = authRateLimiter.getRemainingBlockTime(identifier);
      const minutes = Math.ceil(remainingTime / (1000 * 60));
      return { 
        error: { 
          message: `Too many failed attempts. Please try again in ${minutes} minute(s).` 
        }
      };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      // Record the attempt
      const rateLimit = authRateLimiter.recordAttempt(identifier, !error);
      
      if (error) {
        if (rateLimit.blocked) {
          return { 
            error: { 
              message: 'Too many failed attempts. Account temporarily locked for security.' 
            }
          };
        } else if (rateLimit.remainingAttempts <= 2) {
          return { 
            error: { 
              message: `${error.message} (${rateLimit.remainingAttempts} attempts remaining)`
            }
          };
        }
      }
      
      return { error };
    } catch (error) {
      console.error('Sign in exception:', error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/`
        },
      });
      
      return { error };
    } catch (error) {
      console.error('Sign up exception:', error);
      return { error };
    }
  };

  const signOut = async () => {
    // Clear the inactivity timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      signIn,
      signUp,
      signOut,
      loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
